/**
 * Renders queued contract-simulation errors (see lib/simulationErrors.ts).
 *
 * Simulation errors come from `lib/previewWrite.ts`: before a state-changing
 * write is broadcast it is previewed with a gas-free `eth_call`, and a revert
 * there means the transaction is never sent. Those reverts are surfaced here
 * rather than only in the inline `error` state of whichever panel fired the
 * write.
 *
 * Mounted once in the app shell. Escape dismisses; clicking the backdrop
 * dismisses the top entry.
 */
import { useEffect, useSyncExternalStore } from 'react';
import {
    clearSimulationErrors,
    dismissSimulationError,
    getSimulationErrors,
    subscribeSimulationErrors,
} from '../../lib/simulationErrors';

export function SimulationErrorModal() {
    const errors = useSyncExternalStore(
        subscribeSimulationErrors,
        getSimulationErrors,
        getSimulationErrors
    );

    // Clear leftovers from a previous mount (e.g. HMR) so a stale revert never
    // greets the user on a fresh page.
    useEffect(() => {
        clearSimulationErrors();
    }, []);

    useEffect(() => {
        if (errors.length === 0) return;
        const onKey = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') dismissSimulationError();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [errors.length]);

    if (errors.length === 0) return null;
    const top = errors[0];
    const remaining = errors.length - 1;

    return (
        <div
            className="sim-error-overlay"
            role="presentation"
            onClick={() => dismissSimulationError()}
        >
            <div
                className="sim-error-modal"
                role="alertdialog"
                aria-modal="true"
                aria-label="Contract simulation error"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sim-error-head">
                    <span className="sim-error-badge">Simulation error</span>
                    {top.contract && <code className="sim-error-contract">{top.contract}</code>}
                </div>
                <h3 className="sim-error-title">{top.title}</h3>
                <pre className="sim-error-message">{top.message}</pre>
                <div className="sim-error-foot">
                    <span className="sim-error-count">
                        {remaining > 0 ? `${remaining} more pending` : 'No transaction was broadcast.'}
                    </span>
                    <div className="sim-error-actions">
                        {remaining > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={clearSimulationErrors}
                            >
                                Dismiss all
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => dismissSimulationError()}
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SimulationErrorModal;
